import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ResellerSBC } from '@app/core/interfaces/sbc';
import { SecurityPermissionName } from '@app/core/models/permission';
import { DialogComponent } from '@app/shared/dialog/dialog-component.directive';
import { AuthorizationService } from '@app/shared/services/authorization.service';
import { PortalError } from '@app/utils/error/portal-error';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { finalize, first } from 'rxjs/operators';

export interface SipCredentialsInput {
  /** `VoIP` is for generic devices, `Trunk` for trunks */
  mode: SipCredentialsMode;
  /** Either VoIP or Trunk SBC. */
  rsbc: ResellerSBC;
  /** Only relevant for trunks, a redundant SBC. */
  rsbc2?: ResellerSBC;
  /** SIP Account actually */
  sipUri?: string;
  /** SIP username, part of `sipUri` */
  sipUsername?: string;
  /** SIP domain, part of `sipUri` */
  sipDomain?: string;
  /** Password for the SIP account */
  sipPassword$: Observable<string>;
}

/** `VoIP` is for generic devices, `Trunk` for trunks */
export enum SipCredentialsMode {
  VoIP,
  Trunk,
}

/**
 * To view SIP credentials of a business trunk and of an end-user generic device.
 */
@UntilDestroy()
@Component({
  selector: 'portal-sip-credentials',
  templateUrl: './sip-credentials.component.html',
  standalone: false,
})
export class SipCredentialsComponent
  extends DialogComponent<SipCredentialsInput, void>
  implements OnInit, OnChanges, SipCredentialsInput
{
  mode!: SipCredentialsMode;
  rsbc!: ResellerSBC;
  rsbc2!: ResellerSBC | null;
  sipUri?: string;
  sipUsername?: string;
  sipDomain?: string;
  sipPassword$: Observable<string>;

  error: PortalError | null = null;
  passwordLoading: boolean = false;
  plainPassword: string | null = null;

  showSbcData: boolean = true;
  showPassword: boolean = true;

  constructor(private authService: AuthorizationService) {
    super();
  }

  ngOnInit(): void {
    // VIEW_SBC relevant for trunk details page component
    this.authService
      .hasPermission$({ anyPermission: [SecurityPermissionName.VIEW_SBC] })
      .pipe(first())
      .subscribe((userCanViewSBC: boolean) => {
        if (this.mode === SipCredentialsMode.Trunk && userCanViewSBC === false) {
          this.showSbcData = false;
        }
      });
    // VIEW_SBC relevant for trunk details page component
    this.authService
      .hasPermission$({ anyPermission: [SecurityPermissionName.VIEW_TRUNK_PASSWORD] })
      .pipe(first())
      .subscribe((userCanViewTrunkPassword: boolean) => {
        if (this.mode === SipCredentialsMode.Trunk && userCanViewTrunkPassword === false) {
          this.showPassword = false;
        } else {
          this.passwordLoading = true;

          this.sipPassword$
            .pipe(
              finalize(() => {
                this.passwordLoading = false;
              })
            )
            .subscribe({
              error: (error) => {
                this.error = error;
              },
              next: (password) => {
                this.plainPassword = password;
              },
            });
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {}
}
